import { useEffect, useId, useRef, useState, type InputHTMLAttributes, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Copy } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { errorCode } from '../lib/api'

/** Champ de formulaire étiqueté, avec aide et erreur reliées par ARIA. */
export function Field({ label, hint, error, ...input }: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string; error?: string }) {
  const id = useId()
  const describedBy = [hint && `${id}-hint`, error && `${id}-error`].filter(Boolean).join(' ') || undefined
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {hint && (
        <span id={`${id}-hint`} className="hint">
          {hint}
        </span>
      )}
      <input id={id} aria-describedby={describedBy} aria-invalid={error ? true : undefined} {...input} />
      {error && (
        <span id={`${id}-error`} className="hint" style={{ color: 'var(--danger)' }}>
          {error}
        </span>
      )}
    </div>
  )
}

export function Alert({ kind, children }: { kind: 'error' | 'success' | 'warning'; children: ReactNode }) {
  return (
    <div className={`alert ${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  )
}

/** Message d'erreur traduit à partir du code renvoyé par l'API. */
export function ErrorAlert({ error }: { error: unknown }) {
  const { t } = useTranslation()
  if (!error) return null
  return <Alert kind="error">{t(`errors.${errorCode(error)}`)}</Alert>
}

/** Bouton qui demande une confirmation claire (dialogue modal natif) avant une action sensible. */
export function ConfirmButton({
  children,
  message,
  onConfirm,
  className = 'btn danger',
}: {
  children: ReactNode
  message: string
  onConfirm: () => void | Promise<void>
  className?: string
}) {
  const { t } = useTranslation()
  const ref = useRef<HTMLDialogElement>(null)
  return (
    <>
      <button type="button" className={className} onClick={() => ref.current?.showModal()}>
        {children}
      </button>
      <dialog ref={ref}>
        <p>{message}</p>
        <div className="row">
          <button
            type="button"
            className="btn danger"
            onClick={async () => {
              ref.current?.close()
              await onConfirm()
            }}
          >
            {t('common.confirm')}
          </button>
          <button type="button" className="btn ghost" onClick={() => ref.current?.close()} autoFocus>
            {t('common.cancel')}
          </button>
        </div>
      </dialog>
    </>
  )
}

export function CopyButton({ value }: { value: string }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      className="btn secondary"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value)
          setCopied(true)
          setTimeout(() => setCopied(false), 2000)
        } catch {
          /* presse-papiers refusé : la valeur reste affichée et sélectionnable */
        }
      }}
    >
      {copied ? <Check aria-hidden size={20} /> : <Copy aria-hidden size={20} />}
      <span aria-live="polite">{copied ? t('common.copied') : t('common.copy')}</span>
    </button>
  )
}

/**
 * Attente d'une page (session, code chargé à la demande). Rien pendant 0,4 s (pas de clignotement quand ça va
 * vite), puis « Chargement… » centré, annoncé aux lecteurs d'écran.
 */
export function PageLoading() {
  const { t } = useTranslation()
  const [shown, setShown] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setShown(true), 400)
    return () => clearTimeout(timer)
  }, [])
  return (
    <div className="page-loading" role="status" aria-live="polite">
      {shown && (
        <>
          <span className="spinner" aria-hidden />
          {t('common.loading')}
        </>
      )}
    </div>
  )
}

/** Le serveur ne répond pas (après les nouveaux essais automatiques) : le dire, et proposer de réessayer. */
export function ServerUnreachable() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  return (
    <section className="stack">
      <h1>{t('common.serverUnreachableTitle')}</h1>
      <Alert kind="error">{t('errors.network_error')}</Alert>
      <button type="button" className="btn" style={{ justifySelf: 'start' }} onClick={() => void qc.resetQueries({ queryKey: ['me'] })}>
        {t('common.retry')}
      </button>
    </section>
  )
}
